import { useCallback, useState } from 'react';
import { useQuery, useMutation, useApolloClient } from 'react-apollo';
import { useDispatch } from 'react-redux';
import { useLocation } from 'react-router-dom';
import qs from 'query-string';

import {
  accountCreateByEmail as accountCreateByEmailMutation,
  accountUpdate as accountUpdateMutation,
} from '../../../../graphql/mutations/account';
import {
  accountPaymentMethodCreate as accountPaymentMethodCreateMutation,
  checkoutPhotoManagerSubscription as checkoutPhotoManagerSubscriptionMutation,
  checkoutPhotoFinderSubscription as checkoutPhotoFinderSubscriptionMutation,
} from '../../../../graphql/mutations/checkout';
import { openAlert } from '../../../../actions/modal-actions';
import { getErrorString } from '../../../../util/normalize-errors';
import { sentryCaptureMessage } from '../../../../util/sentry';
import { loginSuccess } from '../../../../actions/auth-actions';
import { ACCOUNT_PRO_ROLES } from '../../../../constants/account-roles';
import getPhotoManagerSubscriptionItems from '../../../../graphql/queries/getPhotoManagerSubscriptionItems';
import {
  getDefaultTrialPhotoManagerSubscriptionItem,
  getProRegistrationStoreKeyValue,
  PRO_REGISTRATION_PERSONAS,
  PRO_REGISTRATION_STEP,
} from '../helpers';
import { AccountPromoCodeVerify } from '../../../../graphql/queries/account-referrals';
import { gtm } from '../../../../util/analytics';
import getPhotoFinderSubscriptionItems from '../../../../graphql/queries/getPhotoFinderSubscriptionItems';
import { accountUsernameExists } from '../../../../graphql/queries/account';
import { PHOTO_MANAGER_SUBSCRIPTION_BILLING_PERIOD } from '../../../../apps/pro/views/Account/AccountPhotoManagerSettings/components/PhotoManagerSubscriptionCheckout/helpers';
import { ACCOUNT_REFERRAL_TYPE } from '../../../../constants/account-referrals';
import { formatPriceForDisplay } from '../../../../util/format-money';

const PHOTO_FINDER_TIERS = {
  TIER_1: 1,
  TIER_2: 2,
  TIER_3: 3,
  TIER_4: 4,
  TIER_5: 5,
};
/**
 * Pro registration hook. Performs the entire flow & registration process.
 *
 * @returns {{
 *  loading: Boolean,
 *  defaultPersona: String,
 *  photoManagerSubscriptionItems: Array<Object>,
 *  photoFinderSubscriptionItems: Array<Object>,
 *  defaultPhotoFinderTier: String,
 *  defaultPhotoManagerItem: Object,
 *  activeStep: String,
 *  canGoBack: Boolean,
 *  loadingSubscriptionItems: Boolean,
 *  accountPromoCode: Object,
 *  onSubmit: Function,
 *  onAccountPromoCodeVerify: Function,
 *  onAccountUsernameVerify: Function,
 *  onGoBack: Function
 *  }}
 */
const useProRegister = () => {
  const [loading, setLoading] = useState(false);
  const client = useApolloClient();
  const location = useLocation();
  const dispatch = useDispatch();
  const onErrorModalOpen = useCallback(state => dispatch(openAlert(state)), [
    dispatch,
  ]);
  const onLogin = useCallback(() => dispatch(loginSuccess()));
  const [accountCreateByEmail] = useMutation(accountCreateByEmailMutation);
  const [accountUpdate] = useMutation(accountUpdateMutation);
  const [accountPaymentMethodCreate] = useMutation(
    accountPaymentMethodCreateMutation,
  );
  const [checkoutPhotoManagerSubscription] = useMutation(
    checkoutPhotoManagerSubscriptionMutation,
  );
  const [checkoutPhotoFinderSubscription] = useMutation(
    checkoutPhotoFinderSubscriptionMutation,
  );

  const {
    data: { photoManagerSubscriptionItems = [] },
    loading: loadingPhotoManagerSubscriptionItems,
  } = useQuery(getPhotoManagerSubscriptionItems);
  const {
    data: { photoFinderSubscriptionItems = [] },
    loading: loadingPhotoFinderSubscriptionItems,
  } = useQuery(getPhotoFinderSubscriptionItems);

  const defaultTrialPhotoManagerSubscriptionItem = getDefaultTrialPhotoManagerSubscriptionItem(
    photoManagerSubscriptionItems,
  );

  const {
    role: accountRole = ACCOUNT_PRO_ROLES.PHOTOGRAPHER,
    persona: defaultPersona,
    source = '',
    tier = '',
    billing_period: billingPeriod = PHOTO_MANAGER_SUBSCRIPTION_BILLING_PERIOD.YEARLY.toLowerCase(),
    free_trial: isFreeTrial,
    promo_code: defaultPromoCode,
  } = qs.parse(location.search ?? '') ?? {};

  const [activeStep, setActiveStep] = useState(
    isFreeTrial
      ? PRO_REGISTRATION_STEP.FREE_TRIAL
      : PRO_REGISTRATION_STEP.PHOTO_MANAGER_SUBSCRIPTION_SELECTION,
  );
  const [accountPromoCode, setAccountPromoCode] = useState(null);
  const defaultPhotoFinderTier =
    tier && billingPeriod
      ? photoFinderSubscriptionItems.find(
          item =>
            item.tier === PHOTO_FINDER_TIERS[tier.toUpperCase?.()] &&
            item.billing_period === billingPeriod.toUpperCase?.(),
        )
      : null;
  const defaultPhotoManagerItem =
    !defaultPromoCode &&
    billingPeriod &&
    ['monthly', 'yearly'].includes(billingPeriod)
      ? photoManagerSubscriptionItems.find(
          item => item.billing_period === billingPeriod.toUpperCase?.(),
        )
      : null;
  /**
   * Verifies a promo code.
   *
   * @param {string} promoCode The promo code.
   * @returns {Promise<boolean|*|boolean>}
   */
  const onAccountPromoCodeVerify = async promoCode => {
    setLoading(true);

    try {
      const { data: { accountPromoCodeVerify } = {} } = await client.query({
        query: AccountPromoCodeVerify,
        variables: {
          promoCode,
        },
      });

      return accountPromoCodeVerify;
    } catch {
      return false;
    } finally {
      setLoading(false);
    }
  };

  const onAccountUsernameVerify = async username => {
    setLoading(true);

    try {
      const {
        data: { accountUsernameExists: accountUsernameExistsResult } = {},
      } = await client.query({
        query: accountUsernameExists,
        variables: {
          username,
        },
      });

      return accountUsernameExistsResult;
    } catch {
      return false;
    } finally {
      setLoading(false);
    }
  };

  /**
   * Handles the logic of registering and checking out the user.
   *
   * @param {Object} values The regiser form's values.
   * @returns {Promise<void>}
   */
  const onRegister = async values => {
    if (loading) {
      return;
    }

    setLoading(true);
    const personaObj = PRO_REGISTRATION_PERSONAS.find(
      p => p.value === values.persona,
    );

    try {
      const response = await accountCreateByEmail({
        variables: {
          input: {
            email: values.email_address,
            name: `${values.first_name} ${values.last_name}`.trim(),
            password: values.password,
            username: values.username,
          },
        },
      });
      const accountId = response.data.accountCreateByEmail.account_id;
      const clientStoreItems = [
        {
          key: 'pro_registration_mode',
          value: getProRegistrationStoreKeyValue(values),
        },
      ];

      if (values.persona) {
        clientStoreItems.push({
          key: 'pro_registration_persona',
          value: values.persona,
        });
      }

      await accountUpdate({
        variables: {
          input: {
            id: accountId,
            pro_roles: personaObj
              ? [personaObj.role.toUpperCase()]
              : [accountRole.toUpperCase()],
            organization_name: values.organization_name,
            client_store_items: clientStoreItems,
            state_id: values.state_id ?? undefined,
          },
        },
      });

      const selectedSubscriptionItem = photoManagerSubscriptionItems?.find(
        item => item.id === values.plan_id,
      );
      const selectedPhotoFinderSubscriptionItem = photoFinderSubscriptionItems?.find(
        item => item.id === values.tier_id,
      );

      // The user is checking out the plan_id (yearly free trial PM sub)
      if (values.stripe_token && !isFreeTrial) {
        await checkoutPhotoManagerSubscription({
          variables: {
            input: {
              photo_manager_subscription_item_id: values.plan_id,
              email_address: values.email_address,
              stripe_token: values.stripe_token.id,
              promo_code: values.promo_code,
              // If the user subbed for photo finder, free trial is skipped.
              skip_free_trial: !!values.tier_id,
            },
          },
        });

        gtm.trackCustomEvent({
          event: 'SubscriptionPurchase',
          package: values.tier_id
            ? `PM_${selectedSubscriptionItem?.billing_period}`
            : `PM_${selectedSubscriptionItem?.billing_period}_TRIAL`,
          value: values.tier_id
            ? formatPriceForDisplay(selectedSubscriptionItem?.price?.value)
            : 0,
          user_id: accountId,
        });

        // Legacy -- Should be removed after 5/15/2022
        gtm.trackCustomEvent({
          event: 'ProPurchase',
          package: values.tier_id
            ? `PM_${selectedSubscriptionItem?.billing_period}`
            : `PM_${selectedSubscriptionItem?.billing_period}_TRIAL`,
          value: values.tier_id
            ? formatPriceForDisplay(selectedSubscriptionItem?.price?.value)
            : 0,
        });

        // The user has checked out with a photo finder subscription
        if (selectedPhotoFinderSubscriptionItem) {
          await checkoutPhotoFinderSubscription({
            variables: {
              input: {
                photo_finder_subscription_item_id: values.tier_id,
                email_address: values.email_address,
                stripe_token: values.stripe_token.id,
              },
            },
          });

          gtm.trackCustomEvent({
            event: 'SubscriptionPurchase',
            package: `PF_${selectedPhotoFinderSubscriptionItem.billing_period}`,
            value: formatPriceForDisplay(
              selectedPhotoFinderSubscriptionItem?.price?.value,
            ),
            user_id: accountId,
          });
        }
      }

      // The user is on the monthly Free Trial
      if (isFreeTrial) {
        // The user is on monthly free trial but with a credit card
        if (values.stripe_token) {
          await accountPaymentMethodCreate({
            variables: {
              input: {
                stripe_token: values.stripe_token.id,
                email_address: values.email_address,
              },
            },
          });
        }

        // This needs to run with or without a credit card
        await checkoutPhotoManagerSubscription({
          variables: {
            input: {
              photo_manager_subscription_item_id:
                values.plan_id ?? defaultTrialPhotoManagerSubscriptionItem.id,
              email_address: values.email_address,
            },
          },
        });

        gtm.trackCustomEvent({
          event: 'SubscriptionPurchase',
          package: `PM_${
            selectedSubscriptionItem?.billing_period ??
            defaultTrialPhotoManagerSubscriptionItem?.billing_period
          }_TRIAL`,
          value: 0,
          user_id: accountId,
        });

        // Legacy -- Should be removed after 5/15/2022
        gtm.trackEvent({
          type: 'FreeTrialRegistration',
          action: getProRegistrationStoreKeyValue(values),
          label: accountRole ?? 'Pro Photographer',
          source,
        });
      }

      gtm.trackEvent({
        type: 'Register as PRO',
        action: getProRegistrationStoreKeyValue(values),
        label: accountRole ?? 'Pro Photographer',
        source,
      });

      // Forces log in and redirect to /app
      onLogin();
    } catch (responseError) {
      onErrorModalOpen({
        message: 'There was an error with your registration',
        info: responseError?.request_id ?? getErrorString(responseError),
      });

      sentryCaptureMessage('PRO Registration Error', {
        level: 'error',
        extra: {
          error: responseError,
          email: values.email_address,
        },
      });

      setLoading(false);
    }
  };

  const onPromoCodeValidate = async promoCode => {
    if (promoCode) {
      const validatedPromoCode = await onAccountPromoCodeVerify(promoCode);

      if (
        validatedPromoCode.is_valid &&
        [
          ACCOUNT_REFERRAL_TYPE.PHOTO_MANAGER_YEARLY_50_PC_OFF_ONCE,
          ACCOUNT_REFERRAL_TYPE.PHOTO_MANAGER_YEARLY,
          ACCOUNT_REFERRAL_TYPE.PHOTO_MANAGER_MONTHLY,
        ].includes(validatedPromoCode.type)
      ) {
        setAccountPromoCode(validatedPromoCode);
        return validatedPromoCode;
      }

      setAccountPromoCode(null);
      return null;
    }

    setAccountPromoCode(null);
    return null;
  };

  const onSubmit = async (values, { setFieldValue }) => {
    const promoCode = await onPromoCodeValidate(values.promo_code);

    if (promoCode) {
      // Force to set a plan based on the Promo code.
      setFieldValue(
        'plan_id',
        promoCode.type === ACCOUNT_REFERRAL_TYPE.PHOTO_MANAGER_MONTHLY
          ? photoManagerSubscriptionItems.find(
              item =>
                item.billing_period ===
                PHOTO_MANAGER_SUBSCRIPTION_BILLING_PERIOD.MONTHLY,
            )?.id
          : photoManagerSubscriptionItems.find(
              item =>
                item.billing_period ===
                PHOTO_MANAGER_SUBSCRIPTION_BILLING_PERIOD.YEARLY,
            )?.id,
      );
    }

    switch (activeStep) {
      case PRO_REGISTRATION_STEP.FREE_TRIAL: {
        onRegister(values);

        return;
      }

      case PRO_REGISTRATION_STEP.PHOTO_MANAGER_SUBSCRIPTION_SELECTION: {
        if (promoCode?.trial_days) {
          setActiveStep(PRO_REGISTRATION_STEP.DETAILS);
        } else {
          setActiveStep(
            PRO_REGISTRATION_STEP.PHOTO_FINDER_SUBSCRIPTION_SELECTION,
          );
        }

        break;
      }

      case PRO_REGISTRATION_STEP.PHOTO_FINDER_SUBSCRIPTION_SELECTION: {
        setActiveStep(PRO_REGISTRATION_STEP.DETAILS);
        break;
      }

      case PRO_REGISTRATION_STEP.DETAILS: {
        setActiveStep(PRO_REGISTRATION_STEP.CART);
        break;
      }

      case PRO_REGISTRATION_STEP.CART:
      default: {
        onRegister(values);
        break;
      }
    }
  };

  const canGoBack = (() => {
    switch (activeStep) {
      case PRO_REGISTRATION_STEP.DETAILS:
      case PRO_REGISTRATION_STEP.CART:
        return true;
      default:
        return false;
    }
  })();

  const onGoBack = () => {
    switch (activeStep) {
      case PRO_REGISTRATION_STEP.DETAILS:
        setActiveStep(
          PRO_REGISTRATION_STEP.PHOTO_MANAGER_SUBSCRIPTION_SELECTION,
        );

        break;
      case PRO_REGISTRATION_STEP.CART:
        setActiveStep(PRO_REGISTRATION_STEP.DETAILS);

        break;
      default:
        break;
    }
  };

  return {
    loading,
    defaultPersona,
    photoManagerSubscriptionItems,
    photoFinderSubscriptionItems,
    defaultPhotoFinderTier,
    defaultPhotoManagerItem,
    activeStep,
    canGoBack,
    loadingSubscriptionItems:
      loadingPhotoFinderSubscriptionItems ||
      loadingPhotoManagerSubscriptionItems,
    accountPromoCode,
    onSubmit,
    onAccountPromoCodeVerify,
    onAccountUsernameVerify,
    onGoBack,
    defaultPromoCode,
  };
};

export default useProRegister;
